import {
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Skeleton,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import { DynamicIcon, Tooltip } from 'src/components';
import { FlatAccordion } from 'src/components/FlatAccordion/flat-accordion';
import { sectionExpandIconWrapperSx } from 'src/components/FlatAccordion/section-expand-icon-sx';
import { SidePanelCaption } from 'src/components/SidePanelCaption';
import { sidePanelCaptionActionButtonSx } from 'src/components/SidePanelRow';
import { Conversation, PartnerSetting, usePartnerSetting } from 'src/graphql';
import { useConversationSummary } from 'src/graphql/conversation-summary';
import { useAISummaryVisibility } from 'src/graphql/entities/ai-summary-visibility/use-ai-summary-visibility';
import {
  CONVERSATION_CARD_PROPERTY_KEYS,
  SidePanelPersonalizationContext,
  useSectionVisibility,
} from 'src/graphql/entities/side-panel-personalization';
import { useCopyToClipboard } from 'src/utils/hooks';

const AccordionSummaryStyled = styled(AccordionSummary)(({ theme }) => ({
  cursor: 'default !important',
  margin: '0.125rem 0 0.25rem',
  '&&': {
    minHeight: 0,
    padding: '0.25rem 0',
    transition: 'none',
    '&.Mui-expanded': {
      minHeight: 0,
    },
  },
  '&& .MuiAccordionSummary-content': {
    cursor: 'default',
    margin: 0,
    transition: 'none',
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  '&& .MuiAccordionSummary-expandIconWrapper': {
    cursor: 'pointer',
    ...sectionExpandIconWrapperSx(theme),
    // the shared wrapper is sized for a section header; on a caption line it would
    // set the row's height instead of the caption doing it
    width: '1.125rem',
    height: '1.125rem',
    marginLeft: '0.5rem',
  },
}));

interface ConversationSummaryProps {
  conversation: Conversation;
}

const SummaryTextContainer = ({
  text,
  loading = false,
}: {
  text: string;
  loading?: boolean;
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box sx={{ padding: '0.125rem 0 0.375rem', marginTop: theme.utils.important(0) }}>
        {[80, 85, 90, 95, 100].map((width) => (
          <Skeleton key={`skeleton-${width}`} variant="text" height={24} width={`${width}%`} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '0.125rem 0 0.375rem', marginTop: theme.utils.important(0) }}>
      <Box
        sx={{
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          fontSize: theme.typography.fontSize,
          lineHeight: 1.5,
        }}
      >
        <ReactMarkdown
          components={{
            p: ({ node, ...props }) => <p style={{ margin: 0 }} {...props} />,
          }}
        >
          {text}
        </ReactMarkdown>
      </Box>
    </Box>
  );
};

export const ConversationSummary = ({ conversation }: ConversationSummaryProps) => {
  const theme = useTheme();

  const { copyToClipboard } = useCopyToClipboard();
  const isVisible = useSectionVisibility('conversation');
  const showSummary = isVisible(CONVERSATION_CARD_PROPERTY_KEYS.summary);
  // isVisible answers true until the layout resolves, so a hidden Summary is
  // only knowable after this settles
  const personalizationLoading =
    useContext(SidePanelPersonalizationContext)?.viewModel.loading ?? false;

  const { data: settings, loading: isLoadingSettings } = usePartnerSetting(
    PartnerSetting.AiConversationSummaryEnabled,
    true,
  );

  const isEnabled =
    settings?.value?.toLowerCase() === 'true' && conversation.lastMessageReceivedAt;

  // generating a summary is a write, so never call it speculatively
  const { conversationSummary, loading: isLoadingSummary } = useConversationSummary(
    conversation?.id,
    conversation?.partnerId,
    isLoadingSettings || personalizationLoading || !isEnabled || !showSummary,
  );

  const { changeAISummaryVisibility, currentAISummaryVisibility } = useAISummaryVisibility();

  const handleCopySummary = useCallback(() => {
    if (conversationSummary?.shortSummary) {
      void copyToClipboard(conversationSummary.shortSummary);
    }
  }, [conversationSummary?.shortSummary, copyToClipboard]);

  const isLoading = isLoadingSummary || isLoadingSettings;

  const onChangeAISummaryVisibility = useCallback(
    (_: React.SyntheticEvent, isExpanded: boolean) => {
      changeAISummaryVisibility(isExpanded);
    },
    [changeAISummaryVisibility],
  );

  if (!isEnabled || personalizationLoading || !showSummary) {
    return;
  }

  return (
    <FlatAccordion
      expanded={currentAISummaryVisibility}
      onChange={onChangeAISummaryVisibility}
      elevation={0}
    >
      <AccordionSummaryStyled
        expandIcon={<DynamicIcon name="chevron-down" library="lucide" size={14} />}
      >
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          flexGrow={1}
          // Prevent accordion from closing when clicking inside accordion summary
          onClick={(event) => event.stopPropagation()}
        >
          <SidePanelCaption>Summary</SidePanelCaption>

          {!isLoading ? (
            <Stack direction="row" flexWrap="nowrap" alignItems="center" gap={1}>
              <Typography
                variant="subtitle2"
                fontSize="0.75rem"
                color={theme.palette.text.secondary}
                sx={{ ml: 'auto' }}
              >
                Generated by AI
              </Typography>

              <Tooltip title="Copy summary">
                <IconButton
                  onClick={handleCopySummary}
                  size="small"
                  sx={sidePanelCaptionActionButtonSx}
                >
                  <DynamicIcon name="copy" library="lucide" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : undefined}
        </Stack>
      </AccordionSummaryStyled>
      <AccordionDetails sx={{ padding: 0 }}>
        <SummaryTextContainer
          loading={isLoading}
          text={conversationSummary?.shortSummary || ''}
        />
      </AccordionDetails>
    </FlatAccordion>
  );
};

SummaryTextContainer.displayName = 'SummaryTextContainer';
